import { useEffect, useRef, useState } from 'react';

/**
 * Drag-to-resize for a rail docked on the RIGHT edge of a layout (the wizard's
 * Robbie panel). Until the user drags, the rail is `defaultShare` of the
 * window and `share` is null; from the first drag on it is the share the user
 * chose, remembered per browser under the caller's key and shared by every RFQ.
 * A share rather than px so it looks the same on a laptop and a large monitor.
 * Double-click resets.
 *
 * The rail can never take the page from the work beside it, nor be squeezed
 * past usable: the floor is the larger of `minPx` and `minShare` of the
 * window, the ceiling the smaller of `maxPx` and `maxShare`. `railStyle`
 * repeats both in CSS, so resizing the window keeps the rail in range
 * without any JS running.
 *
 * The drag works in shares too: the pointer and the window are both in screen
 * px, so their ratio needs no zoom correction. Only the px floor/ceiling and
 * `shareCss` know about buyer-web's root zoom.
 */

/** Arrow-key step, as a share of the window. */
const KEY_STEP_SHARE = 0.01;

export interface ResizableRailOptions {
  storageKey: string;
  /** Hard floor in CSS px — the rail's content breaks below it. */
  minPx: number;
  maxPx: number;
  /** Narrowest the user can drag it, as a share of the window (0–1). */
  minShare: number;
  /** Width before the user has dragged, as a share of the window (0–1). */
  defaultShare: number;
  /** Widest the user can drag it, as a share of the window (0–1). */
  maxShare: number;
}

/** Wizard Robbie rail: opens at its narrowest (20% of the screen), drags out to 45%. */
export const ROBBIE_RAIL_RESIZE: ResizableRailOptions = {
  storageKey: 'hr.wizard.robbieRailWidth',
  minPx: 230,
  maxPx: 1200,
  minShare: 0.2,
  defaultShare: 0.2,
  maxShare: 0.45,
};

/** A share of the window in CSS — `vw` is scaled by the root zoom, so divide it back out. */
const shareCss = (share: number): string => `calc(${share * 100}vw / var(--app-zoom, 1))`;

/** buyer-web's root zoom (`--app-zoom` in index.css): 1.1 on desktop, 1 on a phone. */
const appZoom = (): number =>
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--app-zoom')) || 1;

/**
 * The draggable range as shares of the window — `minShare`/`maxShare` tightened
 * by the px floor and ceiling, which the root zoom makes larger on screen. When
 * the window is too small for both, the floor wins, as CSS min-width does.
 */
type ShareRange = { min: number; max: number };

const shareLimits = ({ minPx, maxPx, minShare, maxShare }: ResizableRailOptions): ShareRange => {
  const zoom = appZoom();
  const pxShare = (px: number) => (px * zoom) / window.innerWidth;
  const min = Math.max(minShare, pxShare(minPx));
  return { min, max: Math.max(min, Math.min(maxShare, pxShare(maxPx))) };
};

/** Into range, to 4 places (under 0.2px on a 1920px screen). */
const clampShare = (share: number, { min, max }: ShareRange): number =>
  Math.round(Math.min(Math.max(share, min), max) * 10000) / 10000;

/**
 * A missing, unreadable or non-share value means "never resized" — including
 * a px width saved before widths were shares. The CSS min/max still clamp a
 * share saved on another screen size.
 */
const readShare = (storageKey: string): number | null => {
  try {
    const stored = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored > 0 && stored <= 1 ? stored : null;
  } catch {
    return null;
  }
};

const writeShare = (storageKey: string, share: number | null) => {
  try {
    if (share === null) window.localStorage.removeItem(storageKey);
    else window.localStorage.setItem(storageKey, String(share));
  } catch {
    // Private mode / blocked storage: the width simply is not remembered.
  }
};

export interface RailResizeHandleBindings {
  /** True while a pointer drag is in progress — the handle stays lit. */
  active: boolean;
  /** aria-value* — percent of the window. */
  valueMin: number;
  valueMax: number;
  valueNow: number;
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
}

export interface ResizableRailState {
  /** Attach to the rail's own wrapper. */
  railRef: React.RefObject<HTMLDivElement | null>;
  /** Inline width/min/max for the wrapper — the saved share, or the default. */
  railStyle: React.CSSProperties;
  handle: RailResizeHandleBindings;
}

export const useResizableRail = (options: ResizableRailOptions): ResizableRailState => {
  const { storageKey, minPx, maxPx, minShare, defaultShare, maxShare } = options;
  const [share, setShare] = useState<number | null>(() => readShare(storageKey));
  const [active, setActive] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startShare: number; range: ShareRange; latest: number } | null>(null);

  const minCss = `max(${minPx}px, ${shareCss(minShare)})`;
  const maxCss = `min(${maxPx}px, ${shareCss(maxShare)})`;
  /** The share the rail covers on screen now — after the CSS min/max. */
  const renderedShare = (): number => {
    const el = railRef.current;
    return el ? el.getBoundingClientRect().width / window.innerWidth : share ?? defaultShare;
  };

  // While dragging, the cursor and the no-text-selection rule belong to the
  // whole page — the pointer spends most of a drag over the step content.
  useEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    const previous = { cursor: root.style.cursor, userSelect: root.style.userSelect };
    root.style.cursor = 'col-resize';
    root.style.userSelect = 'none';
    return () => {
      root.style.cursor = previous.cursor;
      root.style.userSelect = previous.userSelect;
    };
  }, [active]);

  const commit = (next: number | null) => {
    setShare(next);
    writeShare(storageKey, next);
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setActive(false);
    // A click without movement (e.g. to focus the handle) saves nothing.
    if (drag.latest !== drag.startShare) commit(drag.latest);
  };

  const limits = shareLimits(options);
  const toPercent = (value: number) => Math.round(value * 100);
  const handle: RailResizeHandleBindings = {
    active,
    valueMin: toPercent(limits.min),
    valueMax: toPercent(limits.max),
    valueNow: toPercent(clampShare(share ?? defaultShare, limits)),
    onPointerDown: (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      const range = shareLimits(options);
      const startShare = clampShare(renderedShare(), range);
      dragRef.current = { startX: event.clientX, startShare, range, latest: startShare };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setActive(true);
    },
    onPointerMove: (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      // The rail is on the right: moving the pointer left widens it.
      drag.latest = clampShare(drag.startShare + (drag.startX - event.clientX) / window.innerWidth, drag.range);
      // Straight to the DOM, not through state: re-rendering the whole wizard
      // on every move left the edge trailing the pointer. State catches up
      // once, on release.
      if (railRef.current) railRef.current.style.width = shareCss(drag.latest);
    },
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onKeyDown: (event) => {
      const range = shareLimits(options);
      const now = clampShare(renderedShare(), range);
      const next =
        event.key === 'ArrowLeft' ? now + KEY_STEP_SHARE
        : event.key === 'ArrowRight' ? now - KEY_STEP_SHARE
        : event.key === 'Home' ? range.min
        : event.key === 'End' ? range.max
        : null;
      if (next === null) return;
      event.preventDefault();
      commit(clampShare(next, range));
    },
    onDoubleClick: () => commit(null),
  };

  return {
    railRef,
    railStyle: { width: shareCss(share ?? defaultShare), minWidth: minCss, maxWidth: maxCss },
    handle,
  };
};

export default useResizableRail;
